import React, { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Button,
  Rate,
  message,
  Select,
  Popconfirm,
  Avatar,
  Tabs,
  Modal,
  Spin,
} from "antd";
import {
  SearchOutlined,
  ReloadOutlined,
  DeleteOutlined,
  StarOutlined,
  UserOutlined,
  ShopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import ShopRatingServ from "../../service/ShopRatingServ";
import Breadcrumbs from "../../component/Breadcrumbs";
import EmptyState from "../../util/EmptyState";
import DataTable from "../../component/ui/DataTable";
import { getImageUrl } from "../../component/SafeImage";
import { useAdminTable } from "../hooks/useAdminTable";

const { Option } = Select;

const ShopRatings = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [detailsVisible, setDetailsVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [activeTab, setActiveTab] = useState("All");
  const topRef = React.useRef(null);

  const fetchRatings = async (params) => {
    const apiParams = {
      limit: params.limit,
      skip: (params.page - 1) * params.limit,
      search: params.search || undefined,
      score: params.score || undefined,
    };
    const response = await ShopRatingServ.adminAll(apiParams);
    return { data: response?.data || [], total: response?.total || 0 };
  };

  const {
    data: ratings,
    total: totalRatings,
    loading,
    params,
    setParams,
    handleSearch,
    refresh,
  } = useAdminTable(fetchRatings, "admin_shop_ratings", {
    page: 1,
    limit: 10,
    score: null,
    search: location.state?.search || "",
  });

  const handleDelete = async (record) => {
    try {
      await ShopRatingServ.remove(record._id);
      message.success("Rating removed successfully");
      refresh();
      setDetailsVisible(false);
    } catch (err) {
      message.error("Failed to remove rating");
    }
  };

  const handleTabChange = (key) => {
    setActiveTab(key);
    setParams((prev) => ({ ...prev, score: key === "All" ? null : parseInt(key), page: 1 }));
  };

  const handleReset = () => {
    setActiveTab("All");
    setParams({ page: 1, limit: 10, score: null, search: "" });
  };

  const starColor = (score) => {
    if (score >= 4) return "#22c55e";
    if (score === 3) return "#f59e0b";
    return "#ef4444";
  };

  const columns = [
    {
      title: "Shop",
      key: "shop",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <Avatar
            src={record.shop?.imageUrl ? getImageUrl(record.shop.imageUrl) : null}
            icon={<ShopOutlined style={{ color: "#64748b" }} />}
            size={38}
            shape="square"
            style={{ borderRadius: 8, border: "1px solid #e2e8f0", background: "#f8fafc", flexShrink: 0 }}
          />
          <div>
            <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>
              {record.shop?.name || "Unknown Shop"}
            </div>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>
              {record.shop?.shopId || ""}
            </div>
          </div>
        </div>
      ),
    },
    {
      title: "Customer",
      key: "user",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Avatar
            src={record.user?.imageUrl ? getImageUrl(record.user.imageUrl) : null}
            icon={<UserOutlined style={{ color: "#64748b" }} />}
            size="small"
            style={{ background: "#f1f5f9", border: "1px solid #e2e8f0" }}
          />
          <div>
            <div style={{ fontWeight: 600, color: "#475569", fontSize: 12 }}>
              {record.user?.name?.trim() || "N/A"}
            </div>
            <div style={{ fontSize: 10, color: "#94a3b8" }}>{record.user?.email || ""}</div>
          </div>
        </div>
      ),
    },
    {
      title: "Rating",
      dataIndex: "score",
      key: "score",
      width: 140,
      align: "center",
      render: (score) => (
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 2 }}>
          <Rate disabled value={score} style={{ fontSize: 13, color: starColor(score) }} />
          <span style={{ fontSize: 11, fontWeight: 700, color: starColor(score) }}>{score}/5</span>
        </div>
      ),
    },
    {
      title: "Comment",
      dataIndex: "review",
      key: "review",
      render: (review) =>
        review ? (
          <span
            style={{
              fontSize: 12,
              color: "#475569",
              fontStyle: "italic",
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            "{review}"
          </span>
        ) : (
          <span style={{ fontSize: 12, color: "#cbd5e1" }}>No comment</span>
        ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      width: 110,
      render: (date) => (
        <span style={{ fontSize: 12, color: "#64748b" }}>
          {dayjs(date).format("DD MMM YYYY")}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: 100,
      render: (_, record) => (
        <Button
          type="primary"
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            setSelectedRecord(record);
            setDetailsVisible(true);
          }}
          style={{
            borderRadius: 3,
            fontWeight: 600,
            background: "#ffffffff",
            color: "#004754ff",
            border: "1px solid #e4e4e7",
            boxShadow: "0 0 0 1px #e4e4e7",
            height: "auto",
            padding: "0px 8px",
            fontSize: 13,
          }}
        >
          View
        </Button>
      ),
    },
  ];

  const statusTabs = [
    { key: "All", label: "All Ratings" },
    { key: "5", label: "⭐⭐⭐⭐⭐ 5 Stars" },
    { key: "4", label: "⭐⭐⭐⭐ 4 Stars" },
    { key: "3", label: "⭐⭐⭐ 3 Stars" },
    { key: "2", label: "⭐⭐ 2 Stars" },
    { key: "1", label: "⭐ 1 Star" },
  ];

  return (
    <>
      <div className="pcu-padding-24" ref={topRef}>
        <div className="pcu-margin-bottom-16">
          <Breadcrumbs />
        </div>

        <div className="sd-container" style={{ padding: "24px", background: "white", borderRadius: "16px", boxShadow: "0 4px 12px rgba(0,0,0,0.05)" }}>
          {/* Header */}
          <div className="sd-header" style={{ marginBottom: "24px", background: "transparent", padding: 0, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
              <div style={{ width: 48, height: 48, background: "#fff", borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 2px 8px rgba(0,0,0,0.05)", border: "1px solid #f1f5f9" }}>
                <StarOutlined style={{ fontSize: 22, color: "#f59e0b" }} />
              </div>
              <div>
                <h1 style={{ margin: 0, fontSize: 20, fontWeight: 800, color: "#1a1d2e", letterSpacing: "-0.5px" }}>Shop Ratings & Comments</h1>
                <p style={{ margin: 0, fontSize: 12, color: "#9ca3af", fontWeight: 500 }}>{totalRatings} total reviews</p>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div style={{ marginBottom: 24, borderBottom: "1px solid #f1f5f9" }}>
            <Tabs
              activeKey={activeTab}
              onChange={handleTabChange}
              items={statusTabs}
              className="premium-tabs-minimal"
              style={{ marginBottom: -1 }}
            />
          </div>

          {/* Filters */}
          <div style={{ background: "transparent", border: "none", padding: "0 0 16px 0", display: "grid", gridTemplateColumns: "repeat(3, 1fr) auto", gap: 12, alignItems: "flex-end" }}>
            <div>
              <span style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, letterSpacing: "0.5px", display: "block", marginBottom: 4 }}>SEARCH</span>
              <div style={{ position: "relative" }}>
                <SearchOutlined style={{ position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)", color: "#94a3b8", zIndex: 1 }} />
                <input
                  placeholder="Shop name, customer or comment..."
                  value={params.search || ""}
                  onChange={(e) => handleSearch(e.target.value)}
                  style={{ width: "100%", height: 36, paddingLeft: 32, paddingRight: 12, border: "1px solid #e2e8f0", borderRadius: 8, fontSize: 13, color: "#1e293b", outline: "none" }}
                />
              </div>
            </div>

            <div>
              <span style={{ color: "#94a3b8", fontSize: 10, fontWeight: 800, letterSpacing: "0.5px", display: "block", marginBottom: 4 }}>FILTER BY STARS</span>
              <Select
                placeholder="All Stars"
                value={params.score || undefined}
                onChange={(v) => setParams((p) => ({ ...p, score: v, page: 1 }))}
                allowClear
                style={{ width: "100%", height: 36 }}
              >
                {[5, 4, 3, 2, 1].map((s) => (
                  <Option key={s} value={s}>{s} Star{s !== 1 ? "s" : ""}</Option>
                ))}
              </Select>
            </div>

            <div />

            <div style={{ display: "flex", alignItems: "flex-end" }}>
              <button
                onClick={handleReset}
                style={{ height: 36, padding: "0 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, background: "#fff", border: "1px solid #e2e8f0", color: "#64748b", display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}
              >
                <ReloadOutlined style={{ fontSize: 13 }} />
                Reset
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="pcu-border-top">
            <DataTable
              columns={columns}
              data={ratings}
              rowKey="_id"
              loading={loading}
              onRow={(record) => ({ style: { cursor: "default" } })}
              locale={{
                emptyText: (
                  <EmptyState
                    title="No Shop Ratings Found"
                    description={params.search ? "No ratings match your search." : "No shop ratings collected yet."}
                    gradient="yellow"
                    icon="star"
                  />
                ),
              }}
              pagination={{
                current: params.page,
                pageSize: params.limit,
                total: totalRatings,
                onShowSizeChange: (c, s) => setParams((p) => ({ ...p, limit: s, page: 1 })),
                onChange: (p) => {
                  setParams((prev) => ({ ...prev, page: p }));
                  topRef.current?.scrollIntoView({ behavior: "smooth" });
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Detail Modal */}
      <Modal
        open={detailsVisible}
        onCancel={() => setDetailsVisible(false)}
        footer={null}
        width={550}
        centered
        className="premium-modal"
      >
        {selectedRecord && (
          <div style={{ padding: "8px 0" }}>
            {/* Shop Header */}
            <div style={{ display: "flex", alignItems: "center", gap: 14, marginBottom: 20 }}>
              <Avatar
                src={selectedRecord.shop?.imageUrl ? getImageUrl(selectedRecord.shop.imageUrl) : null}
                icon={<ShopOutlined />}
                size={56}
                shape="square"
                style={{ borderRadius: 10, border: "1px solid #f1f5f9", background: "#f8fafc" }}
              />
              <div>
                <div style={{ fontSize: 16, fontWeight: 800, color: "#1a1d2e" }}>
                  {selectedRecord.shop?.name || "Unknown Shop"}
                </div>
                <div style={{ fontSize: 11, color: "#94a3b8" }}>
                  {selectedRecord.shop?.shopId} &bull; {dayjs(selectedRecord.createdAt).format("DD MMM YYYY, hh:mm A")}
                </div>
                <Rate disabled value={selectedRecord.score} style={{ fontSize: 15, color: starColor(selectedRecord.score), marginTop: 4 }} />
                <span style={{ fontSize: 13, fontWeight: 700, color: starColor(selectedRecord.score), marginLeft: 6 }}>
                  {selectedRecord.score}/5
                </span>
              </div>
            </div>

            {/* Customer */}
            <div style={{ background: "#f8fafc", borderRadius: 10, padding: "10px 14px", marginBottom: 12, border: "1px solid #f1f5f9" }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Customer</div>
              <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                <Avatar
                  src={selectedRecord.user?.imageUrl ? getImageUrl(selectedRecord.user.imageUrl) : null}
                  icon={<UserOutlined style={{ color: "#64748b" }} />}
                  style={{ background: "#f1f5f9", border: "1px solid #e2e8f0" }}
                />
                <div>
                  <div style={{ fontWeight: 700, color: "#1e293b", fontSize: 13 }}>{selectedRecord.user?.name?.trim() || "N/A"}</div>
                  <div style={{ fontSize: 11, color: "#64748b" }}>{selectedRecord.user?.email || "No email"}</div>
                </div>
              </div>
            </div>

            {/* Review */}
            <div style={{ background: "#fffbeb", borderRadius: 10, padding: "10px 14px", border: "1px solid #fef3c7", marginBottom: 20 }}>
              <div style={{ fontSize: 9, fontWeight: 800, color: "#94a3b8", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 6 }}>Review Comment</div>
              <div style={{ fontSize: 13, color: "#475569", lineHeight: 1.6, fontStyle: selectedRecord.review ? "normal" : "italic" }}>
                {selectedRecord.review ? `"${selectedRecord.review}"` : "No comment provided."}
              </div>
            </div>

            {/* Footer */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", paddingTop: 12, borderTop: "1px solid #f1f5f9" }}>
              <Popconfirm
                title="Remove this rating?"
                onConfirm={() => handleDelete(selectedRecord)}
                okText="Yes, Delete"
                cancelText="Cancel"
              >
                <Button
                  danger
                  icon={<DeleteOutlined />}
                  style={{ fontWeight: 700, height: 38, borderRadius: 8, background: "#fff1f2", border: "1px solid #fecdd3" }}
                >
                  Delete Rating
                </Button>
              </Popconfirm>
              <button
                onClick={() => setDetailsVisible(false)}
                style={{ height: 38, padding: "0 20px", borderRadius: 8, fontSize: 13, fontWeight: 600, background: "#f8fafc", border: "1px solid #e2e8f0", color: "#64748b", cursor: "pointer" }}
              >
                Close
              </button>
            </div>
          </div>
        )}
      </Modal>
    </>
  );
};

export default ShopRatings;
